import { useEffect, useMemo, useRef } from "react";
import { useChatIsStreaming, useChatTextMessages } from "../ChatContext";
import { useScrollAnchor } from "../hooks/useScrollAnchor";
import { useScrollContainer } from "./ScrollContainerContext";
import { useStreamSelector } from "../../useStreamSelector";
import { StreamSelectors } from "../../StreamSelectors";

/**
 * Build a stable fingerprint string for scroll triggers so we only
 * produce a new array reference when the triggers meaningfully change.
 */
function fingerprintTriggers(triggers: unknown[]): string {
  return triggers
    .map((t) => {
      if (t && typeof t === "object" && "content" in t && "status" in t) {
        // Todo-shaped: fingerprint on content + status
        return `${(t as any).content}:${(t as any).status}`;
      }
      return JSON.stringify(t);
    })
    .join("|");
}

export interface ScrollAnchorProps {
  behavior?: ScrollBehavior;
  disabled?: boolean;
  /** Minimum ms between auto-scrolls during streaming. */
  throttleMs?: number;
  /** Extra scroll triggers (e.g. todos). Changes to this array trigger scroll. */
  scrollTriggers?: unknown[];
}

export function ScrollAnchor({
  behavior = "smooth",
  disabled = false,
  throttleMs,
  scrollTriggers,
}: ScrollAnchorProps) {
  const textMessages = useChatTextMessages();
  const isStreaming = useChatIsStreaming();
  const stateError = useStreamSelector(StreamSelectors.stateError);
  const anchorRef = useRef<HTMLDivElement>(null);
  const containerRef = useScrollContainer();

  // Fingerprint scroll triggers so new array references with identical
  // content don't cause unnecessary scroll events.
  const triggerFingerprint = useMemo(
    () => (scrollTriggers?.length ? fingerprintTriggers(scrollTriggers) : ""),
    [scrollTriggers]
  );

  const stableTriggers = useRef(scrollTriggers);
  const prevFingerprint = useRef(triggerFingerprint);
  if (triggerFingerprint !== prevFingerprint.current) {
    stableTriggers.current = scrollTriggers;
    prevFingerprint.current = triggerFingerprint;
  }

  // The error banner sits BELOW the last message in the list but isn't
  // a message itself — appearance changes are pure state transitions
  // and don't show up in `textMessages`. Without an explicit signal,
  // ScrollAnchor's effect doesn't fire on banner mount and the user
  // can be left scrolled to a position where the banner is below the
  // viewport. Fingerprint the error identity so a new failure (or a
  // newly-cleared error) counts as a scroll trigger.
  const errorSignal = stateError
    ? `${stateError.kind}:${stateError.node}:${stateError.message}`
    : "";

  // Combine text messages + external triggers + error signal into a
  // single scroll signal.
  const scrollItems = useMemo(
    () => {
      const base = stableTriggers.current?.length
        ? [...textMessages, ...stableTriggers.current]
        : textMessages;
      return errorSignal ? [...base, errorSignal] : base;
    },
    [textMessages, triggerFingerprint, errorSignal]
  );

  if (containerRef) {
    return (
      <SmartScrollAnchor
        anchorRef={anchorRef}
        containerRef={containerRef}
        messages={scrollItems}
        behavior={behavior}
        disabled={disabled}
        throttleMs={throttleMs}
        isStreaming={isStreaming}
      />
    );
  }

  return <SimpleScrollAnchor anchorRef={anchorRef} messages={scrollItems} behavior={behavior} disabled={disabled} />;
}

function SimpleScrollAnchor({
  anchorRef,
  messages,
  behavior,
  disabled,
}: {
  anchorRef: React.RefObject<HTMLDivElement | null>;
  messages: unknown[];
  behavior: ScrollBehavior;
  disabled: boolean;
}) {
  useEffect(() => {
    if (disabled) return;
    anchorRef.current?.scrollIntoView({ behavior });
  }, [messages, behavior, disabled, anchorRef]);

  return <div ref={anchorRef} aria-hidden="true" className="h-4 shrink-0" />;
}

function SmartScrollAnchor({
  anchorRef,
  containerRef,
  messages,
  behavior,
  disabled,
  throttleMs,
  isStreaming,
}: {
  anchorRef: React.RefObject<HTMLDivElement | null>;
  containerRef: React.RefObject<HTMLElement | null>;
  messages: unknown[];
  behavior: ScrollBehavior;
  disabled: boolean;
  throttleMs?: number;
  isStreaming: boolean;
}) {
  const { shouldAutoScroll, newTurnCount, scrollToBottom } = useScrollAnchor(
    containerRef,
    anchorRef,
    // The hook only fingerprints/counts the array; scroll triggers may mix
    // in non-message values (todos), so the prop is unknown[].
    messages as Parameters<typeof useScrollAnchor>[2],
    { behavior, isStreaming, ...(throttleMs !== undefined && { throttleMs }) }
  );

  return (
    <>
      {newTurnCount > 0 && !shouldAutoScroll && !disabled && (
        <div className="sticky bottom-2 z-10 flex justify-center pointer-events-none">
          <button
            onClick={scrollToBottom}
            className="pointer-events-auto
                       bg-secondary-500 text-white text-xs px-3 py-1.5
                       rounded-full shadow-md hover:bg-secondary-600
                       transition-opacity animate-in fade-in"
          >
            {newTurnCount} new message{newTurnCount > 1 ? "s" : ""} ↓
          </button>
        </div>
      )}
      <div ref={anchorRef} aria-hidden="true" className="h-4 shrink-0" />
    </>
  );
}
